import { useEffect, useRef, useState } from "react";
import { LifeBuoy, X, Plus, Send, ArrowLeft, Loader2, MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const STATUS_META = {
  open: { label: "Open", cls: "bg-volt/15 text-volt" },
  in_progress: { label: "In Progress", cls: "bg-[#f59e0b]/15 text-[#f59e0b]" },
  waiting_user: { label: "Waiting for You", cls: "bg-yellow-400/15 text-yellow-300" },
  resolved: { label: "Resolved", cls: "bg-emerald-500/15 text-emerald-400" },
  closed: { label: "Closed", cls: "bg-zinc-700/40 text-zinc-400" },
};

export function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-zinc-700 text-zinc-300" };
  return <span data-testid="support-status-badge" className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

export const CATEGORIES = [
  { key: "general", label: "General" },
  { key: "billing", label: "Billing" },
  { key: "bot_setup", label: "Bot Setup" },
  { key: "bug", label: "Bug Report" },
  { key: "other", label: "Other" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.key, c.label]));

export function CategoryBadge({ category }) {
  if (!category) return null;
  return <span data-testid="support-category-badge" className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-zinc-700/50 text-zinc-300">{CAT_LABEL[category] || category}</span>;
}

export function Stars({ value = 0, size = 16 }) {
  return (
    <span className="inline-flex gap-0.5" data-testid="support-stars-display">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} style={{ width: size, height: size }}
          className={n <= value ? "fill-yellow-400 text-yellow-400" : "text-zinc-600"} />
      ))}
    </span>
  );
}

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function SupportPanel({ open, onClose }) {
  const { user } = useAuth();
  const [view, setView] = useState("list");       // list | new | thread
  const [requests, setRequests] = useState([]);
  const [active, setActive] = useState(null);      // {request, messages}
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [ratingVal, setRatingVal] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const loadList = () => api.get("/support/requests").then((r) => setRequests(r.data)).catch(() => {});
  const openThread = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/support/requests/${id}`);
      setActive(data); setView("thread");
      loadList();
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { loadList(); setView("list"); } }, [open]);

  // Poll: refresh list (and the open thread) every 15s while the panel is open.
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      loadList();
      if (view === "thread" && active?.request?.id) {
        api.get(`/support/requests/${active.request.id}`).then((r) => setActive(r.data)).catch(() => {});
      }
    }, 15000);
    return () => clearInterval(t);
  }, [open, view, active?.request?.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [active?.messages?.length, view]);

  const submitNew = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Please fill in both fields."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/support/requests", { subject, message, category });
      toast.success(`Request ${data.ref} submitted`);
      setSubject(""); setMessage(""); setCategory("general");
      await loadList();
      openThread(data.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/support/requests/${active.request.id}/messages`, { message: reply });
      setReply("");
      await openThread(active.request.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submitRating = async () => {
    if (!ratingVal) { toast.error("Please pick a star rating."); return; }
    setBusy(true);
    try {
      await api.post(`/support/requests/${active.request.id}/rating`, { rating: ratingVal, comment: ratingComment });
      toast.success("Thanks for your feedback!");
      setRatingVal(0); setRatingComment("");
      await openThread(active.request.id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex justify-end" data-testid="support-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-zinc-950 border-l border-zinc-800 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="h-14 border-b border-zinc-800 flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-2 font-head font-semibold">
            {view !== "list" ? (
              <button data-testid="support-back" onClick={() => { setView("list"); setActive(null); }} className="p-1 -ml-1 text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></button>
            ) : <LifeBuoy className="w-5 h-5 text-[#f59e0b]" />}
            {view === "new" ? "New Request" : view === "thread" ? (active?.request?.ref || "Request") : "Contact Staff"}
          </div>
          <button data-testid="support-close" onClick={onClose} className="p-1 text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {/* LIST */}
        {view === "list" && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-zinc-800/70 bg-gradient-to-br from-[#f59e0b]/5 to-transparent">
              <h3 className="font-head font-semibold">Need help?</h3>
              <p className="text-sm text-zinc-400 mt-1">Need help with the dashboard or your Discord bot? Contact our support team.</p>
              <button data-testid="support-new-btn" onClick={() => setView("new")}
                className="mt-3 inline-flex items-center gap-2 bg-[#f59e0b] text-volt-ink font-semibold text-sm rounded-md px-4 py-2 hover:bg-[#0ea5c4] transition-colors">
                <Plus className="w-4 h-4" /> Contact Staff
              </button>
            </div>
            <div className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500">My Support Requests</div>
            <div className="divide-y divide-zinc-800/70">
              {requests.length === 0 && <p data-testid="support-empty" className="text-zinc-600 text-sm px-4 py-10 text-center">No support requests yet.</p>}
              {requests.map((r) => (
                <button key={r.id} data-testid="support-request-item" onClick={() => openThread(r.id)}
                  className="w-full text-left px-4 py-3 hover:bg-zinc-900/60 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-zinc-500 font-mono">{r.ref}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <CategoryBadge category={r.category} />
                  </div>
                  <div className="text-sm font-medium mt-1 flex items-center gap-2">
                    <span className="truncate">{r.subject}</span>
                    {r.unread && <span data-testid="support-unread-dot" className="w-2 h-2 rounded-full bg-[#f59e0b] shrink-0" />}
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {r.last_reply_by === "admin" ? "Staff replied" : "You replied"} · {fmt(r.last_reply_at)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* NEW */}
        {view === "new" && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div>
              <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Category</label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {CATEGORIES.map((c) => (
                  <button key={c.key} data-testid={`support-category-${c.key}`} onClick={() => setCategory(c.key)}
                    className={`text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${category === c.key ? "bg-[#f59e0b] text-volt-ink border-[#f59e0b]" : "border-zinc-700 text-zinc-300 hover:bg-zinc-900"}`}>{c.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Subject</label>
              <input data-testid="support-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200}
                placeholder="Short description of the issue"
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-[#f59e0b] outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Message</label>
              <textarea data-testid="support-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={8} maxLength={5000}
                placeholder="Explain your problem in detail…"
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-[#f59e0b] outline-none resize-none" />
            </div>
            <div className="flex gap-2">
              <button data-testid="support-submit-btn" onClick={submitNew} disabled={busy || !subject.trim() || !message.trim()}
                className="flex-1 bg-[#f59e0b] text-volt-ink font-semibold text-sm rounded-md px-4 py-2.5 hover:bg-[#0ea5c4] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit Request
              </button>
              <button data-testid="support-cancel-btn" onClick={() => setView("list")}
                className="px-4 py-2.5 rounded-md text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900">Cancel</button>
            </div>
          </div>
        )}

        {/* THREAD */}
        {view === "thread" && active && (
          <>
            <div className="px-4 py-3 border-b border-zinc-800/70 shrink-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">{active.request.subject}</span>
                <StatusBadge status={active.request.status} />
              </div>
              <div className="mt-1"><CategoryBadge category={active.request.category} /></div>
              {active.request.assigned_name && <p className="text-[11px] text-zinc-500 mt-0.5">Handled by {active.request.assigned_name}</p>}
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>}
              {active.messages.map((m) => (
                <div key={m.id} data-testid="support-message" className={`flex ${m.sender_type === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 ${m.sender_type === "user" ? "bg-volt text-volt-ink" : "bg-zinc-800 text-zinc-100"}`}>
                    <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_type === "user" ? "You" : (m.sender_name || "Support")}</div>
                    <p className="text-sm whitespace-pre-wrap break-words">{m.content}</p>
                    <div className="text-[9px] opacity-60 mt-1 text-right">{fmt(m.created_at)}</div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            {(active.request.status === "resolved" || active.request.status === "closed") && (
              active.request.rating ? (
                <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-900/40 text-center shrink-0" data-testid="support-rating-done">
                  <div className="text-xs text-zinc-400">You rated this support</div>
                  <div className="flex justify-center mt-1"><Stars value={active.request.rating} size={18} /></div>
                  {active.request.rating_comment && <p className="text-xs text-zinc-500 mt-1 italic">“{active.request.rating_comment}”</p>}
                </div>
              ) : (
                <div className="px-4 py-3 border-t border-zinc-800 bg-gradient-to-br from-yellow-400/5 to-transparent shrink-0" data-testid="support-rating-prompt">
                  <div className="text-sm font-medium text-center">How was your support experience?</div>
                  <div className="flex justify-center gap-1.5 mt-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} data-testid={`support-rate-${n}`} onClick={() => setRatingVal(n)} className="p-0.5 hover:scale-110 transition-transform">
                        <Star className={`w-7 h-7 ${n <= ratingVal ? "fill-yellow-400 text-yellow-400" : "text-zinc-600"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea data-testid="support-rating-comment" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} rows={2} maxLength={1000}
                    placeholder="Add a comment (optional)…"
                    className="mt-2 w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-yellow-400 outline-none resize-none" />
                  <button data-testid="support-rating-submit" onClick={submitRating} disabled={busy || !ratingVal}
                    className="mt-2 w-full bg-yellow-400 text-[#3a2e00] font-semibold text-sm rounded-md px-4 py-2 hover:bg-yellow-300 transition-colors disabled:opacity-50">
                    Submit Rating
                  </button>
                </div>
              )
            )}
            {active.request.status === "closed" ? (
              <div className="p-4 border-t border-zinc-800 text-center text-sm text-zinc-500 shrink-0">
                This request is closed. Start a new request if you need more help.
              </div>
            ) : (
              <div className="p-3 border-t border-zinc-800 shrink-0 flex items-end gap-2">
                <textarea data-testid="support-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={1}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Type your reply…"
                  className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-[#f59e0b] outline-none resize-none max-h-32" />
                <button data-testid="support-reply-send" onClick={sendReply} disabled={busy || !reply.trim()}
                  className="bg-[#f59e0b] text-volt-ink rounded-md p-2.5 hover:bg-[#0ea5c4] transition-colors disabled:opacity-40">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function SupportButton() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const load = () => api.get("/support/requests").then((r) => setUnread((r.data || []).filter((x) => x.unread).length)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!open) load(); }, [open]);
  return (
    <>
      <button data-testid="support-open-btn" onClick={() => setOpen(true)} title="Contact Staff"
        className="relative p-2 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors">
        <LifeBuoy className="w-5 h-5" />
        {unread > 0 && <span data-testid="support-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#f59e0b] text-volt-ink text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
      </button>
      <SupportPanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}

void MessageSquare;
